import type { DragEndEvent } from '@dnd-kit/core'
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ArrowDown, ArrowUp, ChevronDown, GripVertical } from 'lucide-react'
import type { ReactNode } from 'react'

import { PrintCheckBox, PrintColumnCheckboxRow, PrintColumnGroup } from '@/components/app/print-dialog-shell'
import { resolvePrintSortDirection } from '@/components/screens/millsheet/print/home-print-columns'
import type { ColumnConfig } from '@/components/screens/order-matching/print/svg/main-row'
import { cn } from '@/lib/utils'

//
// 帳票の「表示項目設定」。発注照合・ミルシート・加工証明の3画面すべてがこれを使う
// （2026/08/30 「画面ごとに並び替えUIが違う」FBで統一。それ以前は発注照合だけドラッグ＆ドロップ、
// ミルシート・加工証明は上下ボタンだった）。
//
// 列は2ブロックに分けて出す:
//   1. 並び替えできる項目 … ドラッグして順序を決める。この順序がそのまま帳票の列順になる
//   2. 並び順が固定の項目 … ON/OFF だけ。出力順は各画面の fixColumnOrderFor が決める
// 「順番固定」と「その他」を分けて出していた画面もあったが、どちらも利用者が順序を触れない点は
// 同じで区別する意味が無いため1ブロックにまとめている（出力順のロジック自体は変えていない）。
//

type ColumnType = ColumnConfig['type']

export function PrintColumnSettingsSection({
  title,
  subtitle,
  testIdPrefix,
  open,
  onToggleOpen,
  columnConfigs,
  labels,
  reorderableTypes,
  onToggleColumn,
  onReorder,
  onToggleDirection,
}: {
  title: string
  subtitle: ReactNode
  testIdPrefix: string
  open: boolean
  onToggleOpen?: () => void
  columnConfigs: ColumnConfig[]
  labels: Record<ColumnType, string>
  /** ドラッグで並び替えできる列。ここに無い列は「並び順が固定の項目」として ON/OFF だけ出す */
  reorderableTypes: ColumnType[]
  onToggleColumn: (type: ColumnType) => void
  onReorder: (types: ColumnType[]) => void
  /** 渡したときだけ、並び替えできる項目に昇順／降順トグルを出す（並び順をソートに使う画面だけが渡す） */
  onToggleDirection?: (type: ColumnType) => void
}) {
  return (
    <div className="rounded-lg border border-border">
      <div
        className="flex w-full items-center justify-between gap-3 px-3.5 py-3 text-left"
        data-testid={`${testIdPrefix}-toggle`}
      >
        <span>
          <span className="block text-sm font-semibold text-foreground">{title}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">{subtitle}</span>
        </span>
        {onToggleOpen && (
          <button type="button" aria-expanded={open} aria-label={`${title}を開閉`} onClick={onToggleOpen}>
            <ChevronDown className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
          </button>
        )}
      </div>
      {open && (
        <div className="flex flex-col gap-4 border-t border-border p-3">
          <PrintColumnGroup
            title="並び替えできる項目"
            hint={
              onToggleDirection
                ? 'ドラッグで順序を変えられます。上の項目ほど、ミルシートの並び順で優先されます'
                : 'ドラッグで順序を変えられます'
            }
          >
            <ColumnSortableList
              items={columnConfigs.filter((x) => reorderableTypes.includes(x.type)).map((x) => x.type)}
              labels={labels}
              onReorder={onReorder}
              renderToggle={(type) => {
                const config = columnConfigs.find((x) => x.type === type)
                // 省略時の方向は項目ごとに違う（組織設定の項目は昇順、それ以外は降順）ため 'asc' に決め打ちしない
                const direction = resolvePrintSortDirection({ type, direction: config?.direction })
                const active = config?.active ?? false
                return (
                  <div className="flex items-center gap-2">
                    {onToggleDirection && (
                      <button
                        type="button"
                        data-testid="print-column-direction"
                        data-column={type}
                        data-direction={direction}
                        aria-label={`${labels[type]}の並び方向: ${direction === 'asc' ? '昇順' : '降順'}`}
                        title={
                          active
                            ? `押すと${direction === 'asc' ? '降順' : '昇順'}にします`
                            : '表示をONにすると並び方向を変えられます'
                        }
                        aria-disabled={!active}
                        className={cn(
                          'flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted',
                          !active && 'cursor-not-allowed opacity-50',
                        )}
                        onClick={() => {
                          // 表示OFFの列はソートキーにならないため、方向を変えても結果が変わらない
                          if (active) {
                            onToggleDirection(type)
                          }
                        }}
                      >
                        {direction === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />}
                        {direction === 'asc' ? '昇順' : '降順'}
                      </button>
                    )}
                    <label className="flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        className="sr-only"
                        aria-label={`${labels[type]}を表示`}
                        checked={config?.active ?? false}
                        onChange={() => {
                          onToggleColumn(type)
                        }}
                      />
                      <PrintCheckBox checked={config?.active ?? false} small />
                    </label>
                  </div>
                )
              }}
            />
          </PrintColumnGroup>
          <PrintColumnGroup title="並び順が固定の項目" hint="表示するかどうかだけ選べます">
            <div className="grid grid-cols-2 gap-1">
              {/* 並び替え対象でない列は「残り全部」を出す。画面ごとに固定リストを渡す形にすると、
                  新しい列を足したときに設定UIから静かに消えるため（レビューFB 2026/08/30） */}
              {columnConfigs
                .filter((column) => !reorderableTypes.includes(column.type) && column.type !== 'pageNumber')
                .map((column) => (
                  <PrintColumnCheckboxRow
                    key={column.type}
                    label={labels[column.type]}
                    checked={column.active}
                    onToggle={() => {
                      onToggleColumn(column.type)
                    }}
                  />
                ))}
            </div>
          </PrintColumnGroup>
        </div>
      )}
    </div>
  )
}

/**
 * 保存済みの並び順を尊重しつつ、並び替え対象の列だけを新しい順序へ差し替える。
 * 並び替えできない列の位置は動かさない（帳票の出力順は fixColumnOrderFor 側が決めるため）。
 */
export function reorderColumnConfigs(
  columnConfigs: ColumnConfig[],
  reorderableTypes: ColumnType[],
  nextOrder: ColumnType[],
): ColumnConfig[] {
  return [
    ...nextOrder.flatMap((type) => columnConfigs.filter((column) => column.type === type)),
    ...columnConfigs.filter((column) => !reorderableTypes.includes(column.type)),
  ]
}

function ColumnSortableList({
  items,
  labels,
  onReorder,
  renderToggle,
}: {
  items: ColumnType[]
  labels: Record<ColumnType, string>
  onReorder: (items: ColumnType[]) => void
  renderToggle: (item: ColumnType) => ReactNode
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 1 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (over === null || active.id === over.id) {
      return
    }
    const oldIndex = items.indexOf(active.id as ColumnType)
    const newIndex = items.indexOf(over.id as ColumnType)
    if (oldIndex === -1 || newIndex === -1) {
      return
    }
    onReorder(arrayMove(items, oldIndex, newIndex))
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2">
          {items.map((item) => (
            <SortableColumn key={item} item={item} label={labels[item]} toggle={renderToggle(item)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

function SortableColumn({ item, label, toggle }: { item: ColumnType; label: string; toggle: ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 1 : undefined }}
      data-testid="print-column"
      data-column={item}
      className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1.5"
    >
      <span
        {...attributes}
        {...listeners}
        aria-label={`${label}を並び替え`}
        className="cursor-grab text-muted-foreground"
      >
        <GripVertical className="size-4" />
      </span>
      <span className="min-w-0 flex-1 text-sm">{label}</span>
      {toggle}
    </div>
  )
}
